import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Sidebar from './side/Sidebar';
import TopHeader from './side/TopHeader';
import Pagination from './side/Pagination';
import ConfirmPopup from './side/ConfirmPopup';
import ErrorPopup from './side/ErrorPopup';
import UserDetailModal from './UserDetailModal';
import SearchBox from './side/SearchBox';
import '../css/UserManagement.css';
import { SERVER_URL } from '../../config/config';

const UserManagement = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const ITEMS_PER_PAGE = 5;

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const endpoint = searchTerm 
        ? `/admin/user/search?keyword=${encodeURIComponent(searchTerm)}` 
        : `/admin/user`;
      const response = await api.get(endpoint);
      if (response.data.result_code === 200 && response.data.success) {
        setAllUsers(response.data.users);
      } else {
        throw new Error(response.data.message || '사용자 목록을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      setError(err.message || '사용자 목록을 불러오는데 실패했습니다.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []); // 최초 1회만 전체 유저 목록 받아오기

  // 클라이언트에서 필터링
  const filteredUsers = allUsers.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.nickname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleUserDelete = async (userId) => {
    setSelectedUserId(userId);
    const user = allUsers.find(u => u.userId === userId);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await api.delete(`/admin/user/delete/${selectedUserId}?login_id=${localStorage.getItem('adminId')}`);
      if (response.data.result_code === 200 && response.data.success) {
        setAllUsers(allUsers.filter(user => user.userId !== selectedUserId));
        setShowConfirm(false);
        setError('사용자 탈퇴가 완료되었습니다.');
        setShowError(true);
      } else {
        throw new Error(response.data.message || '사용자 삭제에 실패했습니다.');
      }
    } catch (err) {
      setError(err.message || '사용자 삭제에 실패했습니다.');
      setShowError(true);
    }
  };

  const handleUserRowDoubleClick = async (userId) => {
    try {
      const response = await api.get(`/admin/user/detail/${userId}`);
      if (response.data.result_code === 200 && response.data.success) {
        setUserDetail(response.data);
        setShowUserDetail(true);
      } else {
        throw new Error(response.data.message || '사용자 정보를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      setError(err.message || '사용자 정보를 불러오는데 실패했습니다.');
      setShowError(true);
    }
  };

  const getCurrentPageItems = (items) => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return items.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className="user-management">
        <div className="user-content-wrapper">
          <Sidebar />
          <div className="user-main-content">
            <div className="loading">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      {/* <TopHeader /> */}
      <div className="content-wrapper">
        <Sidebar />
        <div className="main-content">
          <div className="header-section">
            <h1>사용자({filteredUsers.length}명)</h1>
            <SearchBox
              onChange={e => setSearchTerm(e.target.value)}
              value={searchTerm}
              placeholder="아이디, 이메일, 닉네임 검색"
              className="search-box"
            />
          </div>

          <div className="table-container">
            <div className="header-row">
              <span>아이디</span>
              <span>이메일</span>
              <span>닉네임</span>
              <span>옵션</span>
            </div>
            <div className="data-entries">
              {filteredUsers.length === 0 ? (
                <div className="table-row">
                  <span className="cell" style={{ fontSize: '18px', gridColumn: '1 / -1', color: '#888' }}>
                    검색된 사용자가 없습니다.
                  </span>
                </div>
              ) : (
                getCurrentPageItems(filteredUsers).map((user) => (
                  <div 
                    key={user.userId} 
                    className="table-row"
                    onDoubleClick={() => handleUserRowDoubleClick(user.userId)}
                  >
                    <span className="cell">{user.username}</span>
                    <span className="cell">{user.email}</span>
                    <span className="cell">{user.nickname}</span>
                    <span className="cell">
                      <button
                        className="delete-button"
                        onClick={() => handleUserDelete(user.userId)}
                      >
                        회원 탈퇴
                      </button>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {showConfirm && (
        <ConfirmPopup
          message={`정말로 이 회원을 탈퇴시키시겠습니까?\n(닉네임 : ${allUsers.find(user => user.userId === selectedUserId)?.nickname})`}
          onConfirm={confirmDelete}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {showError && (
        <ErrorPopup
          message={error}
          onClose={() => setShowError(false)}
        />
      )}

      {showUserDetail && userDetail && (
        <UserDetailModal
          userDetail={userDetail}
          onClose={() => setShowUserDetail(false)}
        />
      )}
    </div>
  );
};

export default UserManagement; 